import { useQuery } from '@tanstack/react-query';
import type { CountLine, CountTaskDto, HoldDto, ItemDto, TraceResult } from '@zenarty/shared';
import {
  ClipboardCheck,
  ClipboardList,
  Plus,
  Search,
  ShieldAlert,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  Field,
  KeyValue,
  PageHeader,
  Stat,
  Tabs,
} from '../components/kit';
import { Dialog, Drawer } from '../components/overlays';
import { ItemPicker, ReasonSelect, useStatuses } from '../components/pickers';
import { DataTable, type Column } from '../components/table';
import { formatDateTime, formatNumber, timeAgo, titleCase } from '../lib/format';
import { nameMap, useAction, useClients, useServerList, useSites } from '../lib/queries';
import { ExpiryBadge, MovementList, StockStatus } from './Inventory';

// ---------------------------------------------------------------- cycle counts

const STATE_TONE = { open: 'info', recount: 'warn', closed: 'ok', cancelled: 'neutral' } as const;

export function CountsPage() {
  const { can } = useAuth();
  const sites = useSites();
  const [siteId, setSiteId] = useState('');
  const [state, setState] = useState<'open' | 'recount' | 'closed'>('open');
  const [openId, setOpenId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  useEffect(() => {
    if (!siteId && sites.data?.items[0]) setSiteId(sites.data.items[0].id);
  }, [sites.data, siteId]);
  const { query, server } = useServerList<CountTaskDto>('counts', '/inventory/counts', {
    siteId,
    state,
  });
  const columns: Column<CountTaskDto>[] = [
    {
      id: 'loc',
      header: 'Location',
      cell: (c) => <span className="primary-cell mono">{c.locationCode}</span>,
    },
    {
      id: 'reason',
      header: 'Why',
      cell: (c) => (
        <span className="tag">{c.reason === 'abc' ? 'ABC schedule' : titleCase(c.reason)}</span>
      ),
    },
    {
      id: 'attempt',
      header: 'Attempt',
      cell: (c) => (c.attempt > 1 ? <Badge tone="warn">Recount</Badge> : 'First count'),
    },
    { id: 'blind', header: 'Mode', cell: (c) => (c.blind ? 'Blind' : 'Shows expected') },
    {
      id: 'state',
      header: 'State',
      cell: (c) => (
        <Badge tone={STATE_TONE[c.state]} dot>
          {titleCase(c.state)}
        </Badge>
      ),
    },
    {
      id: 'var',
      header: 'Variance',
      align: 'right',
      cell: (c) =>
        c.varianceUnits === null ? (
          <span className="faint">—</span>
        ) : c.varianceUnits === 0 ? (
          <span className="ok-text">0</span>
        ) : (
          <span className="danger-text">{c.varianceUnits}</span>
        ),
    },
    {
      id: 'when',
      header: 'Counted',
      cell: (c) =>
        c.countedAt ? (
          `${timeAgo(c.countedAt)} · ${c.countedBy ?? ''}`
        ) : (
          <span className="faint">—</span>
        ),
    },
  ];
  const open = query.data?.items.find((c) => c.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title="Cycle counts"
        description="ABC-scheduled and ad-hoc counts. Counts are blind; any variance triggers one blind recount, and only a confirmed variance adjusts stock."
        actions={
          <>
            <select
              className="select"
              aria-label="Site"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
            >
              {(sites.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {can('inventory:count') ? (
              <Button variant="primary" icon={Plus} onClick={() => setGenerating(true)}>
                Schedule counts
              </Button>
            ) : null}
          </>
        }
      />
      <Tabs
        value={state}
        onChange={setState}
        tabs={[
          { id: 'open', label: 'To count' },
          { id: 'recount', label: 'Awaiting recount' },
          { id: 'closed', label: 'Completed' },
        ]}
      />
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(c) => c.id}
        loading={query.isLoading}
        server={{ ...server, onSearch: () => undefined }}
        onRowClick={(c) => setOpenId(c.id)}
        empty={
          <EmptyState
            icon={ClipboardList}
            title={state === 'open' ? 'Nothing to count right now' : 'No counts here'}
          >
            Schedule ABC counts to keep every location accurate.
          </EmptyState>
        }
      />
      {generating && siteId ? (
        <GenerateCounts siteId={siteId} onClose={() => setGenerating(false)} />
      ) : null}
      {open ? <CountDrawer task={open} onClose={() => setOpenId(null)} /> : null}
    </>
  );
}

function GenerateCounts({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const [max, setMax] = useState('25');
  const [zone, setZone] = useState('');
  const gen = useAction(
    () =>
      api<{ created: number }>('/inventory/counts/generate', {
        method: 'POST',
        body: { siteId, max: Number(max), zone: zone || undefined, blind: true },
      }),
    {
      invalidate: [['counts']],
      success: (r) =>
        r.created ? `${r.created} counts scheduled` : 'Every location is up to date',
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      title="Schedule ABC counts"
      description="Items are ranked by units moved in the last 90 days: A (top 80% of volume) every 30 days, B every 90, C every 180. Locations that are due come first."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={gen.isPending} onClick={() => gen.mutate(undefined)}>
            Schedule
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="How many locations">
          <input className="input" value={max} onChange={(e) => setMax(e.target.value)} />
        </Field>
        <Field label="Zone (optional)">
          <input
            className="input mono"
            value={zone}
            onChange={(e) => setZone(e.target.value.toUpperCase())}
          />
        </Field>
      </div>
    </Dialog>
  );
}

interface EntryLine {
  item: ItemDto | null;
  lot: string;
  expiry: string;
  qty: string;
}

function CountDrawer({ task, onClose }: { task: CountTaskDto; onClose: () => void }) {
  const { can } = useAuth();
  const [lines, setLines] = useState<EntryLine[]>([{ item: null, lot: '', expiry: '', qty: '' }]);
  const editable = task.state === 'open' || (task.state === 'recount' && task.attempt > 1);
  const submit = useAction(
    () =>
      api<{ state: string; variance: number }>(`/inventory/counts/${task.id}/submit`, {
        method: 'POST',
        body: {
          lines: lines
            .filter((l) => l.item)
            .map((l) => ({
              itemId: l.item!.id,
              lot: l.lot || null,
              expiry: l.expiry || null,
              qty: Number(l.qty || 0),
            })),
        },
      }),
    {
      invalidate: [['counts'], ['inventory']],
      success: (r) =>
        r.state === 'recount'
          ? `Variance of ${r.variance}: a blind recount was created`
          : r.variance
            ? 'Recount confirmed: stock adjusted'
            : 'Count matches the book',
      onSuccess: onClose,
    },
  );
  const cancel = useAction(() => api(`/inventory/counts/${task.id}/cancel`, { method: 'POST' }), {
    invalidate: [['counts']],
    success: 'Count cancelled',
    onSuccess: onClose,
  });
  const set = (i: number, patch: Partial<EntryLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const lineTable = (rows: CountLine[]) => (
    <div className="card table-card">
      <table className="table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Lot</th>
            <th className="right">Qty</th>
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((l, i) => (
              <tr key={i}>
                <td className="mono">{l.sku ?? l.itemId.slice(0, 8)}</td>
                <td className="mono">{l.lot ?? '—'}</td>
                <td className="right num">{formatNumber(l.qty)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3} className="muted">
                Empty
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
  return (
    <Drawer
      title={<span className="mono">{task.locationCode}</span>}
      subtitle={`${task.attempt > 1 ? 'Recount' : 'Count'} · ${task.blind ? 'blind' : 'shows expected'} · ${titleCase(task.state)}`}
      icon={
        <span className="icon-tile accent">
          <ClipboardCheck />
        </span>
      }
      onClose={onClose}
      footer={
        editable && can('inventory:count') ? (
          <>
            <Button
              variant="primary"
              loading={submit.isPending}
              onClick={() => submit.mutate(undefined)}
            >
              Submit count
            </Button>
            {can('inventory:approve') ? (
              <Button variant="ghost" onClick={() => cancel.mutate(undefined)}>
                Cancel count
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      {editable ? (
        <section className="stack-sm">
          <div className="section-title">What is physically in {task.locationCode}?</div>
          <p className="muted small">
            List every item you find; leave the location empty if it is empty. Quantities are in
            eaches.
          </p>
          {lines.map((l, i) => (
            <div key={i} className="card" style={{ padding: 12, display: 'grid', gap: 8 }}>
              <ItemPicker value={l.item} onChange={(item) => set(i, { item })} />
              <div className="form-grid-3" style={{ gap: 8 }}>
                <input
                  className="input mono"
                  placeholder="Lot"
                  value={l.lot}
                  disabled={!l.item?.lotControlled}
                  onChange={(e) => set(i, { lot: e.target.value })}
                />
                <input
                  className="input"
                  type="date"
                  value={l.expiry}
                  disabled={!l.item?.expiryControlled}
                  onChange={(e) => set(i, { expiry: e.target.value })}
                />
                <div className="row" style={{ flexWrap: 'nowrap' }}>
                  <input
                    className="input"
                    inputMode="numeric"
                    placeholder="Qty"
                    value={l.qty}
                    onChange={(e) => set(i, { qty: e.target.value })}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Trash2}
                    aria-label="Remove line"
                    onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                  />
                </div>
              </div>
            </div>
          ))}
          <div>
            <Button
              size="sm"
              icon={Plus}
              onClick={() =>
                setLines((ls) => [...ls, { item: null, lot: '', expiry: '', qty: '' }])
              }
            >
              Add item
            </Button>
          </div>
        </section>
      ) : null}
      {task.expected ? (
        <section>
          <div className="section-title">Book quantity</div>
          {lineTable(task.expected)}
        </section>
      ) : task.blind && editable ? (
        <Callout tone="info" title="Blind count">
          The expected quantity is hidden so the count is independent.
        </Callout>
      ) : null}
      {task.counted ? (
        <section>
          <div className="section-title">
            Counted{task.countedBy ? ` by ${task.countedBy}` : ''}
          </div>
          {lineTable(task.counted)}
        </section>
      ) : null}
    </Drawer>
  );
}

// ---------------------------------------------------------------- holds and recalls

export function HoldsPage() {
  const { can } = useAuth();
  const [active, setActive] = useState<'true' | 'false'>('true');
  const [creating, setCreating] = useState(false);
  const clients = useClients();
  const clientName = nameMap(clients.data?.items, (c) => c.name);
  const holds = useQuery({
    queryKey: ['inventory', 'holds', active],
    queryFn: () => api<HoldDto[]>(`/inventory/holds?active=${active}`),
  });
  const release = useAction(
    (id: string) =>
      api<{ unitsReleased: number }>(`/inventory/holds/${id}/release`, {
        method: 'POST',
        body: {},
      }),
    {
      invalidate: [['inventory']],
      success: (r) => `${formatNumber(r.unitsReleased)} units released`,
    },
  );
  const scope = (h: HoldDto) =>
    [
      h.clientId ? clientName.get(h.clientId) : null,
      h.sku ? `item ${h.sku}` : null,
      h.lot ? `lot ${h.lot}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'Location';
  const columns: Column<HoldDto>[] = [
    {
      id: 'what',
      header: 'Scope',
      cell: (h) => (
        <div>
          <div className="primary-cell">{scope(h)}</div>
          <div className="sub">{h.note}</div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (h) =>
        h.isRecall ? (
          <Badge tone="danger" dot>
            Recall
          </Badge>
        ) : (
          <Badge tone="warn" dot>
            Hold
          </Badge>
        ),
    },
    { id: 'status', header: 'Status', cell: (h) => h.statusName },
    { id: 'reason', header: 'Reason', cell: (h) => h.reason ?? '—' },
    {
      id: 'units',
      header: 'Units held',
      align: 'right',
      cell: (h) => (
        <strong>{formatNumber(active === 'true' ? h.unitsStillHeld : h.unitsHeld)}</strong>
      ),
    },
    {
      id: 'when',
      header: active === 'true' ? 'Placed' : 'Released',
      cell: (h) => timeAgo(active === 'true' ? h.createdAt : h.releasedAt),
    },
    {
      id: 'act',
      header: '',
      cell: (h) =>
        active === 'true' && can('inventory:hold') ? (
          <Button
            size="sm"
            icon={ShieldCheck}
            loading={release.isPending}
            onClick={() => release.mutate(h.id)}
          >
            Release
          </Button>
        ) : null,
    },
  ];
  return (
    <>
      <PageHeader
        title="Holds & recalls"
        description="Freeze stock by client, item, lot or location in one step. A recall finds every unit of a lot across your sites and places it on hold."
        actions={
          can('inventory:hold') ? (
            <Button variant="primary" icon={ShieldAlert} onClick={() => setCreating(true)}>
              Place hold or recall
            </Button>
          ) : null
        }
      />
      <Tabs
        value={active}
        onChange={setActive}
        tabs={[
          { id: 'true', label: 'Active' },
          { id: 'false', label: 'Released' },
        ]}
      />
      <DataTable
        columns={columns}
        rows={holds.data ?? []}
        rowKey={(h) => h.id}
        loading={holds.isLoading}
        empty={<EmptyState icon={ShieldCheck} title="No holds" />}
      />
      {creating ? <HoldDialog onClose={() => setCreating(false)} /> : null}
    </>
  );
}

function HoldDialog({ onClose }: { onClose: () => void }) {
  const clients = useClients();
  const statuses = useStatuses();
  const [clientId, setClientId] = useState('');
  const [item, setItem] = useState<ItemDto | null>(null);
  const [lot, setLot] = useState('');
  const [statusId, setStatusId] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [recall, setRecall] = useState(false);
  useEffect(() => {
    const s = statuses.data?.find((x) => x.code === (recall ? 'RECALL' : 'QC_HOLD'));
    if (s) setStatusId(s.id);
  }, [recall, statuses.data]);
  const place = useAction(
    () =>
      api<{ unitsHeld: number; buckets: number }>('/inventory/holds', {
        method: 'POST',
        body: {
          clientId: clientId || null,
          itemId: item?.id ?? null,
          lot: lot || null,
          statusId,
          reasonCodeId: reason,
          note: note || null,
          isRecall: recall,
        },
      }),
    {
      invalidate: [['inventory']],
      success: (r) => `${formatNumber(r.unitsHeld)} units held in ${r.buckets} stock records`,
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      title={recall ? 'Recall a lot' : 'Place a hold'}
      description="All available stock matching every field you fill in is moved to the hold status immediately, at every site you can access."
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={recall ? 'danger-solid' : 'primary'}
            loading={place.isPending}
            disabled={!statusId || !reason || !(clientId || item || lot)}
            onClick={() => place.mutate(undefined)}
          >
            {recall ? 'Recall' : 'Place hold'}
          </Button>
        </>
      }
    >
      <label className="check">
        <input type="checkbox" checked={recall} onChange={(e) => setRecall(e.target.checked)} />{' '}
        <strong>This is a product recall</strong> (FSMA / supplier recall)
      </label>
      <div className="form-grid">
        <Field label="Client">
          <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Any client</option>
            {(clients.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Item">
          <ItemPicker value={item} onChange={setItem} clientId={clientId || undefined} />
        </Field>
        <Field label="Lot">
          <input className="input mono" value={lot} onChange={(e) => setLot(e.target.value)} />
        </Field>
        <Field label="Hold status">
          <select className="select" value={statusId} onChange={(e) => setStatusId(e.target.value)}>
            {(statuses.data ?? [])
              .filter((s) => !s.allocatable)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Reason">
          <ReasonSelect categories={['hold']} value={reason} onChange={setReason} />
        </Field>
        <Field label="Note">
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Supplier notice #114"
          />
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- lot trace

export function TracePage() {
  const sites = useSites();
  const siteName = nameMap(sites.data?.items, (s) => s.code);
  const [lot, setLot] = useState('');
  const [submitted, setSubmitted] = useState('');
  const trace = useQuery({
    queryKey: ['inventory', 'trace', submitted],
    queryFn: () => api<TraceResult>(`/inventory/trace?lot=${encodeURIComponent(submitted)}`),
    enabled: Boolean(submitted),
  });
  const t = trace.data;
  return (
    <div className="stack">
      <PageHeader
        title="Lot trace"
        description="Forward and backward traceability for a lot: where it came from, where it is now, and where it went (FSMA 204 and recall readiness)."
      />
      <Card>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(lot.trim());
          }}
        >
          <div className="input-icon" style={{ flex: 1, minWidth: 240 }}>
            <Search />
            <input
              className="input mono"
              placeholder="Lot number"
              value={lot}
              onChange={(e) => setLot(e.target.value)}
              autoFocus
            />
          </div>
          <Button type="submit" variant="primary" disabled={!lot.trim()}>
            Trace
          </Button>
        </form>
      </Card>
      {t ? (
        t.movements.length || t.balances.length ? (
          <>
            <div className="grid-stats">
              <Stat
                label="Received"
                icon={ClipboardCheck}
                value={formatNumber(t.received)}
                foot={t.items.map((i) => i.sku).join(', ')}
              />
              <Stat
                label="On hand now"
                icon={ClipboardList}
                tone="ok"
                value={formatNumber(t.onHand)}
                foot={`${t.sites.map((s) => siteName.get(s) ?? 'site').join(', ')}`}
              />
              <Stat
                label="Held"
                icon={ShieldAlert}
                tone={t.held ? 'warn' : 'accent'}
                value={formatNumber(t.held)}
              />
              <Stat
                label="Shipped"
                icon={ClipboardCheck}
                value={formatNumber(t.shipped)}
                foot={`${t.shipments.length} shipment movement(s)`}
              />
            </div>
            <Card title="Where it is now" flush>
              <table className="table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Site</th>
                    <th>Location</th>
                    <th>Expiry</th>
                    <th>Status</th>
                    <th className="right">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {t.balances.map((b) => (
                    <tr key={b.id}>
                      <td className="mono">{b.sku}</td>
                      <td>{siteName.get(b.siteId)}</td>
                      <td className="mono">{b.locationCode}</td>
                      <td>
                        <ExpiryBadge expiry={b.expiry} />
                      </td>
                      <td>
                        <StockStatus code={b.statusCode} name={b.statusName} />
                      </td>
                      <td className="right num">{formatNumber(b.qty)}</td>
                    </tr>
                  ))}
                  {!t.balances.length ? (
                    <tr>
                      <td colSpan={6} className="muted">
                        None left in stock.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </Card>
            <Card title="Full history" sub={`${t.movements.length} movements, oldest first`}>
              <MovementList rows={t.movements} />
            </Card>
          </>
        ) : (
          <EmptyState icon={Search} title={`No record of lot ${submitted}`}>
            Check the lot number; lots are case-sensitive.
          </EmptyState>
        )
      ) : null}
      {t ? <KeyValue items={[['Traced at', formatDateTime(new Date().toISOString())]]} /> : null}
    </div>
  );
}
